import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  Popover,
  IconButton,
  Badge,
  CircularProgress,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import {
  getNotifications,
  getUnreadCount,
  markNotificationRead,
  markAllNotificationsRead,
} from "../../../../../api/notifications";

// Bell SVG — no MUI icon dep
const BellIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
    <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
  </svg>
);

const CheckIcon = () => (
  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
    <polyline points="20 6 9 17 4 12"/>
  </svg>
);

const TYPE_COLOR = {
  REQUEST_SUBMITTED: "accent",
  PMO_APPROVED:      "gold",
  SCG_APPROVED:      "accent",
  REJECTED:          "red",
  CANCELLED:         "text3",
};

const TYPE_LABEL = {
  REQUEST_SUBMITTED: "Submitted",
  PMO_APPROVED:      "PMO Approved",
  SCG_APPROVED:      "SCG Approved",
  REJECTED:          "Rejected",
  CANCELLED:         "Cancelled",
};

// Format ISO date string → relative time label
const formatRelativeTime = (isoString) => {
  const diff = Date.now() - new Date(isoString).getTime();
  const mins  = Math.floor(diff / 60000);
  if (mins < 1)   return "just now";
  if (mins < 60)  return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24)   return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
};

const NotificationBell = ({ onBar }) => {
  const theme  = useTheme();
  const t      = theme.custom;
  const isDark = theme.palette.mode === "dark";

  const iconColor       = onBar ? "rgba(255,255,255,0.65)" : t.text3;
  const iconActiveColor = onBar ? "rgba(255,255,255,0.90)" : t.accent;
  const btnBorder       = onBar ? "rgba(255,255,255,0.18)" : t.border2;
  const btnBg           = onBar ? "rgba(255,255,255,0.08)" : "transparent";
  const btnHoverBg      = onBar ? "rgba(255,255,255,0.14)" : t.surface2;

  const [anchorEl,    setAnchorEl]    = useState(null);
  const [items,       setItems]       = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading,     setLoading]     = useState(false);
  const [error,       setError]       = useState(null);

  const open = Boolean(anchorEl);

  // ── Poll unread count for badge (every 60s) ──────────────────────────────
  const fetchUnreadCount = useCallback(async () => {
    try {
      const data = await getUnreadCount();
      setUnreadCount(data.count);
    } catch {
      // Silently fail — badge count is non-critical
    }
  }, []);

  useEffect(() => {
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 60000);
    return () => clearInterval(interval);
  }, [fetchUnreadCount]);

  // ── Fetch full list when popover opens ───────────────────────────────────
  const fetchNotifications = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getNotifications();
      setItems(data);
      setUnreadCount(data.filter((n) => !n.isRead).length);
    } catch {
      setError("Failed to load notifications.");
    } finally {
      setLoading(false);
    }
  }, []);

  const handleOpen = (e) => {
    setAnchorEl(e.currentTarget);
    fetchNotifications();
  };

  const handleClose = () => setAnchorEl(null);

  // ── Mark single read ─────────────────────────────────────────────────────
  const markRead = async (notificationId) => {
    // Optimistic update
    setItems((prev) =>
      prev.map((n) =>
        n.notificationId === notificationId ? { ...n, isRead: true } : n
      )
    );
    setUnreadCount((prev) => Math.max(0, prev - 1));

    try {
      await markNotificationRead(notificationId);
    } catch {
      // Rollback on failure
      setItems((prev) =>
        prev.map((n) =>
          n.notificationId === notificationId ? { ...n, isRead: false } : n
        )
      );
      setUnreadCount((prev) => prev + 1);
    }
  };

  // ── Mark all read ────────────────────────────────────────────────────────
  const markAllRead = async () => {
    // Optimistic update
    setItems((prev) => prev.map((n) => ({ ...n, isRead: true })));
    setUnreadCount(0);

    try {
      await markAllNotificationsRead();
    } catch {
      // Rollback on failure — re-fetch ground truth
      fetchNotifications();
    }
  };

  // ── Resolve type → color ─────────────────────────────────────────────────
  const getColor = (type) => {
    if (type === "REJECTED") return "#c84040";
    const key = TYPE_COLOR[type] || "text3";
    return t[key] || t.text3;
  };

  return (
    <>
      <IconButton
        onClick={handleOpen}
        size="small"
        sx={{
          width:        "34px",
          height:       "34px",
          borderRadius: "4px",
          background:   open ? btnBg : "transparent",
          border:       `1px solid ${open ? btnBorder : "transparent"}`,
          color:        unreadCount > 0 ? iconActiveColor : iconColor,
          transition:   "all 0.18s ease",
          "&:hover": {
            background:  btnHoverBg,
            color:       onBar ? "rgba(255,255,255,0.90)" : t.text2,
            borderColor: btnBorder,
          },
        }}
      >
        <Badge
          badgeContent={unreadCount}
          sx={{
            "& .MuiBadge-badge": {
              background: t.accent,
              color:      isDark ? "#060E0D" : "#fff",
              fontSize:   "8px",
              fontFamily: "'DM Mono', monospace",
              fontWeight: 600,
              minWidth:   "14px",
              height:     "14px",
              padding:    "0 3px",
              top:        "1px",
              right:      "1px",
            },
          }}
        >
          <BellIcon />
        </Badge>
      </IconButton>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top",    horizontal: "right" }}
        slotProps={{
          paper: {
            sx: {
              mt:           "8px",
              width:        "340px",
              background:   t.surface,
              border:       `1px solid ${t.border2}`,
              borderRadius: "8px",
              boxShadow:    isDark
                ? "0 16px 48px rgba(0,0,0,0.6)"
                : "0 8px 32px rgba(3,81,77,0.12)",
              overflow:     "hidden",
            },
          },
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display:        "flex",
            alignItems:     "center",
            justifyContent: "space-between",
            padding:        "12px 16px 11px",
            borderBottom:   `1px solid ${t.border}`,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <Typography
              sx={{
                fontFamily:    "'DM Mono', monospace",
                fontSize:      "10px",
                letterSpacing: "0.14em",
                textTransform: "uppercase",
                color:         t.text2,
                fontWeight:    500,
              }}
            >
              Notifications
            </Typography>
            {unreadCount > 0 && (
              <Box
                sx={{
                  background:   t.accent2,
                  color:        t.accent,
                  borderRadius: "3px",
                  padding:      "1px 6px",
                  fontFamily:   "'DM Mono', monospace",
                  fontSize:     "9px",
                  fontWeight:   600,
                }}
              >
                {unreadCount} new
              </Box>
            )}
          </Box>

          {unreadCount > 0 && (
            <Box
              onClick={markAllRead}
              sx={{
                display:    "flex",
                alignItems: "center",
                gap:        "4px",
                cursor:     "pointer",
                color:      t.text3,
                fontSize:   "10px",
                fontFamily: "'DM Mono', monospace",
                transition: "color 0.15s",
                "&:hover":  { color: t.accent },
              }}
            >
              <CheckIcon />
              <span>Mark all read</span>
            </Box>
          )}
        </Box>

        {/* Notification list */}
        <Box sx={{ maxHeight: "340px", overflowY: "auto" }}>
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", padding: "32px 16px" }}>
              <CircularProgress size={20} sx={{ color: t.accent }} />
            </Box>
          ) : error ? (
            <Box
              sx={{
                padding:    "32px 16px",
                textAlign:  "center",
                color:      "#c84040",
                fontFamily: "'DM Mono', monospace",
                fontSize:   "11px",
              }}
            >
              {error}
            </Box>
          ) : items.length === 0 ? (
            <Box
              sx={{
                padding:    "32px 16px",
                textAlign:  "center",
                color:      t.text3,
                fontFamily: "'DM Mono', monospace",
                fontSize:   "11px",
              }}
            >
              No notifications
            </Box>
          ) : (
            items.map((n, i) => {
              const color = getColor(n.type);
              return (
                <Box
                  key={n.notificationId}
                  onClick={() => !n.isRead && markRead(n.notificationId)}
                  sx={{
                    display:      "flex",
                    gap:          "10px",
                    padding:      "11px 16px",
                    borderBottom: i < items.length - 1
                      ? `1px solid ${t.border}`
                      : "none",
                    background:   n.isRead ? "transparent" : t.surface2,
                    cursor:       n.isRead ? "default" : "pointer",
                    transition:   "background 0.15s",
                    "&:hover":    { background: t.surface3 },
                  }}
                >
                  {/* Color dot */}
                  <Box
                    sx={{
                      width:        "6px",
                      height:       "6px",
                      borderRadius: "50%",
                      background:   n.isRead ? t.border3 : color,
                      flexShrink:   0,
                      mt:           "4px",
                      transition:   "background 0.2s",
                    }}
                  />

                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    {/* Type badge */}
                    <Box
                      sx={{
                        display:       "inline-block",
                        fontFamily:    "'DM Mono', monospace",
                        fontSize:      "8.5px",
                        letterSpacing: "0.1em",
                        textTransform: "uppercase",
                        color:         n.isRead ? t.text3 : color,
                        mb:            "3px",
                        transition:    "color 0.2s",
                      }}
                    >
                      {TYPE_LABEL[n.type] || n.type}
                    </Box>

                    {/* Message */}
                    <Typography
                      sx={{
                        fontSize:   "11.5px",
                        color:      n.isRead ? t.text3 : t.text2,
                        lineHeight: 1.55,
                        fontWeight: n.isRead ? 300 : 400,
                        transition: "color 0.2s",
                      }}
                    >
                      {n.message}
                    </Typography>

                    {/* Timestamp */}
                    <Typography
                      sx={{
                        fontFamily:    "'DM Mono', monospace",
                        fontSize:      "9px",
                        color:         t.text3,
                        mt:            "4px",
                        letterSpacing: "0.04em",
                      }}
                    >
                      {formatRelativeTime(n.createdAt)}
                    </Typography>
                  </Box>
                </Box>
              );
            })
          )}
        </Box>

        {/* Footer */}
        <Box
          sx={{
            borderTop: `1px solid ${t.border}`,
            padding:   "9px 16px",
            textAlign: "center",
          }}
        >
          <Typography
            sx={{
              fontFamily:    "'DM Mono', monospace",
              fontSize:      "9px",
              letterSpacing: "0.12em",
              textTransform: "uppercase",
              color:         t.text3,
              cursor:        "pointer",
              transition:    "color 0.15s",
              "&:hover":     { color: t.accent },
            }}
          >
            View all notifications
          </Typography>
        </Box>
      </Popover>
    </>
  );
};

export default NotificationBell;